import { useMemo } from 'react'
import type { ManCode, ManCodeJournalEntry, UpdatePatch } from '#api/tmsApiService'
import type { ConfigurationStorageDataV2 } from '#features/ManCode/ConfigurationStorageDataV2'
import { readInternalStoredData } from '#features/ManCode/readInternalStoredData'

export interface GroupedFPCChange {
  type: 'fpc'
  path: string
  fields: Record<string, { from: UpdatePatch; to: UpdatePatch }>
}

export interface GroupedOtherChange {
  type: 'other'
  path: string
  from: UpdatePatch | null
  to: UpdatePatch | null
}

export type GroupedChange = GroupedFPCChange | GroupedOtherChange

type HistoryEntry = Required<ConfigurationStorageDataV2>['history'][number]

export type EnrichedJournalEntry = ManCodeJournalEntry & {
  groupedChanges: GroupedChange[]
  changes: (HistoryEntry & { deselections: [] })[]
  deselections: []
}

function enrichJournalEntry(
  entry: ManCodeJournalEntry,
  context: {
    collapseFpcChanges: boolean
    hasShownWarning: boolean
    history: HistoryEntry[]
  },
): EnrichedJournalEntry {
  // we expect that both forwardUpdateOperations & backwardUpdateOperations are present and of the same length
  // and correspond to each other by index and that the order of factory codes is stable
  const from = entry.backwardUpdateOperations || []
  const to = entry.forwardUpdateOperations || []

  const groupedChanges: GroupedChange[] = []
  const fpcChangesMap = new Map<string, GroupedFPCChange>()
  const relevantHistoryEntries: Required<ConfigurationStorageDataV2>['history'][number][] = []

  if (from.length !== to.length) {
    if (!context.hasShownWarning) {
      console.warn('Mismatched forward and backward update operations in journal entry', entry)
      context.hasShownWarning = true
    }
    return {
      ...entry,
      changes: relevantHistoryEntries.map((x) => ({ ...x, deselections: [] })),
      groupedChanges,
      deselections: [],
    }
  }

  for (let i = 0; i < from.length && i < to.length; i++) {
    const fromOp = from[i]
    const toOp = to[i]

    // just bad typing, we check to make typescript happy
    if (!fromOp.path || !toOp.path) {
      continue
    }

    if (fromOp.path !== toOp.path) {
      if (!context.hasShownWarning) {
        console.warn('Mismatched paths in forward and backward update operations in journal entry', entry)
        context.hasShownWarning = true
      }
      continue
    }

    const path = fromOp.path
    const isFPCChange = context.collapseFpcChanges && path.startsWith('/configuration/factoryFPCCodes/')

    if (!isFPCChange) {
      groupedChanges.push({
        type: 'other',
        path,
        from: fromOp,
        to: toOp,
      })
      continue
    }

    const [_s, _c, _f, key, field] = path.split('/')
    let fpcChange = fpcChangesMap.get(key)
    if (!fpcChange) {
      fpcChange = {
        type: 'fpc',
        path: `/configuration/factoryFPCCodes/${key}`,
        fields: {},
      }
      fpcChangesMap.set(key, fpcChange)
      groupedChanges.push(fpcChange)
    }

    fpcChange.fields[field] = {
      from: fromOp,
      to: toOp,
    }
  }

  if (entry.updateTimestamp && context.history) {
    // find all history entries that happened before this journal entry timestamp
    const updateTimestamp = new Date(entry.updateTimestamp).getTime()
    const remainingHistoryEntries: ConfigurationStorageDataV2['history'] = []
    for (const historyEntry of context.history) {
      const historyTimestamp = historyEntry.ended
      if (historyTimestamp <= updateTimestamp) {
        relevantHistoryEntries.push(historyEntry)
      } else {
        remainingHistoryEntries.push(historyEntry)
      }
    }
    context.history = remainingHistoryEntries
  }

  return {
    ...entry,
    changes: relevantHistoryEntries.map((x) => ({ ...x, deselections: [] })),
    groupedChanges,
    deselections: [],
  }
}

export const useEnrichedJournalEntries = (
  manCode: ManCode,
  options?: {
    collapseFpcChanges?: boolean
  },
) => {
  const { collapseFpcChanges = true } = options || {}

  return useMemo((): EnrichedJournalEntry[] => {
    if (!manCode.journal || !manCode.journal.entries) {
      return []
    }

    const internalData = readInternalStoredData(manCode)

    const context = {
      collapseFpcChanges,
      hasShownWarning: false,
      history: internalData?.history ?? [],
    }

    return manCode.journal.entries.map((e) => enrichJournalEntry(e, context)).reverse()
  }, [manCode, collapseFpcChanges])
}
